import { useEffect, useState } from "react";
import { Button, Text } from "../../ui";
import { HeaderImagePicker } from "./HeaderImagePicker";
import { SettingsSection } from "./SettingsSection";
import { useUpdateWorkspace } from "../../../hooks/useWorkspace";
import { canHidePoweredBy } from "../../../lib/plan-limits";
import type { HeaderImage, Workspace } from "../../../lib/api";
import { cn } from "../../../lib/cn";

type AppearanceSectionProps = {
  workspace: Workspace;
  isAdmin: boolean;
};

function headerImagesEqual(a: HeaderImage | null, b: HeaderImage | null) {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.id === b.id;
}

export function AppearanceSection({
  workspace,
  isAdmin,
}: AppearanceSectionProps) {
  const [brandColor, setBrandColor] = useState(workspace.brandColor);
  const [hidePoweredBy, setHidePoweredBy] = useState(
    workspace.hidePoweredBy ?? false,
  );
  const [headerImage, setHeaderImage] = useState<HeaderImage | null>(
    workspace.headerImage ?? null,
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const updateWorkspace = useUpdateWorkspace(workspace.id);

  const canTogglePoweredBy = canHidePoweredBy(workspace.plan);

  useEffect(() => {
    setBrandColor(workspace.brandColor);
    setHidePoweredBy(workspace.hidePoweredBy ?? false);
    setHeaderImage(workspace.headerImage ?? null);
  }, [workspace.brandColor, workspace.hidePoweredBy, workspace.headerImage]);

  async function handleSave() {
    setError(null);
    const result = await updateWorkspace.mutateAsync({
      brandColor,
      hidePoweredBy: canTogglePoweredBy ? hidePoweredBy : undefined,
      headerImage,
    });

    if (!result.success) {
      setError(result.error.message);
      return;
    }
  }

  const hasChanges =
    brandColor !== workspace.brandColor ||
    hidePoweredBy !== (workspace.hidePoweredBy ?? false) ||
    !headerImagesEqual(headerImage, workspace.headerImage ?? null);

  return (
    <SettingsSection
      id="appearance"
      title="Appearance"
      description="Colors and hero styling shown on your public knowledge base."
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-2">
          <Text variant="label">Hero background</Text>
          <Text variant="caption" className="text-foreground/45">
            Full-width background for the hero section on your public knowledge
            base home page.
          </Text>
          <div className="overflow-hidden rounded-xl border border-foreground/10">
            <div
              className={cn(
                "relative flex min-h-56 flex-col items-center justify-center bg-stone px-6 py-10 text-center",
                headerImage && "bg-cover bg-center",
              )}
              style={
                headerImage
                  ? { backgroundImage: `url(${headerImage.url})` }
                  : undefined
              }
            >
              <div className="absolute inset-0 bg-black/35" />
              <div className="relative flex flex-col items-center gap-3">
                <div className="flex flex-col gap-1"></div>
              </div>
            </div>
          </div>

          {headerImage && (
            <Text variant="caption" className="text-foreground/45">
              Photo by{" "}
              <a
                href={headerImage.photographerUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground"
              >
                {headerImage.photographerName}
              </a>{" "}
              on{" "}
              <a
                href="https://unsplash.com/?utm_source=SonderSoft&utm_medium=referral"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground"
              >
                Unsplash
              </a>
            </Text>
          )}

          {isAdmin && (
            <div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPickerOpen(true)}
              >
                {headerImage
                  ? "Change hero background"
                  : "Choose hero background"}
              </Button>
            </div>
          )}
        </div>

        <HeaderImagePicker
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          workspaceId={workspace.id}
          value={headerImage}
          onChange={setHeaderImage}
        />

        <div className="flex flex-col gap-2">
          <Text variant="label">Brand color</Text>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={brandColor}
              onChange={(e) => setBrandColor(e.target.value)}
              disabled={!isAdmin}
              className="h-10 w-14 cursor-pointer rounded-lg border border-foreground/10 bg-white p-1 disabled:cursor-not-allowed disabled:opacity-50"
            />
            <Text variant="body-sm" className="text-foreground/60">
              {brandColor}
            </Text>
          </div>
        </div>

        <div className="flex items-start justify-between gap-4 rounded-xl border border-foreground/10 px-4 py-3">
          <div>
            <Text variant="label">
              Remove &quot;Powered by SonderSoft&quot;
            </Text>
            <Text variant="caption" className="mt-1 text-foreground/45">
              {canTogglePoweredBy
                ? "Hide the SonderSoft branding on your public knowledge base."
                : "Available on the Growth plan and above."}
            </Text>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={hidePoweredBy}
            disabled={!isAdmin || !canTogglePoweredBy}
            onClick={() => setHidePoweredBy((v) => !v)}
            className={cn(
              "relative h-6 w-11 shrink-0 rounded-full transition-colors",
              hidePoweredBy ? "bg-foreground" : "bg-foreground/20",
              (!isAdmin || !canTogglePoweredBy) &&
                "cursor-not-allowed opacity-50",
            )}
          >
            <span
              className={cn(
                "absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white transition-transform",
                hidePoweredBy && "translate-x-5",
              )}
            />
          </button>
        </div>

        {error && (
          <Text variant="caption" className="text-red-600">
            {error}
          </Text>
        )}

        {isAdmin && (
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={handleSave}
              loading={updateWorkspace.isPending}
              disabled={!hasChanges}
            >
              Save appearance
            </Button>
          </div>
        )}
      </div>
    </SettingsSection>
  );
}
